<template>
  <BbPopup v-model="isOpened" :showCloseIcon="true" contentClass="translation-dialog">
    <template #popup-header>
      <TextBody tag="span" i18n-key="translation.translateDocument" :body-type="4" weight="medium" />
    </template>
    <div v-if="!isTranslating" class="select-language">
      <div class="title-hint-container">
        <span class="document-title">{{ referenceNameForTranslate }}</span>
        <TextBody tag="span" i18n-key="translation.chooseLanguage" :body-type="2" />
      </div>
      <div class="title-hint-container" v-if="selectedSourceLanguage === selectedTargetLanguage">
        <TextBody tag="span" i18n-key="translation.sameLanguageError" :body-type="1" />
      </div>
      <BbSelectList
        v-model="selectedSourceLanguage"
        :label="t('translation.sourceLanguage')"
        :isAutocomplete="true"
        width="100%"
      >
        <template #default>
          <BbSelectListItem
            v-for="item in sourceLanguages"
            :key="`select-6-${item.language}`"
            :label="item.name"
            :value="item.language"
          >
            <div class="status-container">
              <BbIcon v-if="selectedSourceLanguage === item.language" size="small" name="checkmark-small" />
              <div class="bb-select-list-item__label">
                <span>{{ item.name }}</span>
                <span>{{ item.language }}</span>
              </div>
            </div>
          </BbSelectListItem>
        </template>
      </BbSelectList>
      <BbSelectList
        v-model="selectedTargetLanguage"
        :label="t('translation.targetLanguage')"
        :isAutocomplete="true"
        width="100%"
      >
        <template #default>
          <BbSelectListItem
            v-for="item in targetLanguages"
            :key="`select-6-${getTargetKey(item)}`"
            :label="item.name"
            :value="getTargetKey(item)"
          >
            <div class="status-container">
              <BbIcon v-if="selectedTargetLanguage === getTargetKey(item)" size="small" name="checkmark-small" />
              <div class="bb-select-list-item__label">
                <span>{{ createTargetLanguageLabel(item) }}</span>
                <span>{{ item.language }}</span>
              </div>
            </div>
          </BbSelectListItem>
        </template>
      </BbSelectList>
      <BbSelectList v-model="selectedGlossary" :label="t('glossaries.glossary')" :isAutocomplete="true" width="100%">
        <template #default>
          <BbSelectListItem v-for="item in glossaries" :key="item.id" :label="item.title" :value="item.id">
            <div class="status-container">
              <BbIcon v-if="selectedGlossary === item.id" size="small" name="checkmark-small" />
              <div class="bb-select-list-item__label">
                {{ item.title }}
              </div>
            </div>
          </BbSelectListItem>
        </template>
      </BbSelectList>
    </div>
    <div v-else class="translating-message">
      <TextBody tag="span" i18n-key="translation.translatingMessage" :body-type="2" />
    </div>
    <template #popup-actions>
      <div v-if="!isTranslating" class="actions-container">
        <BbBtn type="secondary" @click="onClose">
          <TextBody i18n-key="common.cancel" />
        </BbBtn>
        <BbBtn size="large" type="primary" color="#6222e3" @click="onTranslateDocument" :is-disabled="disabledButton">
          <TextBody i18n-key="common.translate" />
        </BbBtn>
      </div>
      <BbBtn v-else class="close-button" size="small" type="secondary" @click="onClose">
        <TextBody i18n-key="common.gotIt" />
      </BbBtn>
    </template>
  </BbPopup>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import useDocuments from '~/composables/useDocuments';
import useGlossaries from '~/composables/useGlossaries';
import useModals from '~/composables/useModals';
import useTranslation from '~/composables/useTranslation';
import useConversations from '~/composables/useConversations';
import { TRANSLATION_TYPE } from '~/constants/documents';
import TextBody from '~/components/common/TextBody';
import { resolveLanguageCodeForSourceList } from '~/utils/document-utils';

export default {
  components: { TextBody },
  setup() {
    const { t } = useI18n();
    const { selectedReference } = useDocuments();
    const { getAvailableGlossaries } = useGlossaries();
    const { translateDocument } = useConversations();
    const {
      filesModalState,
      hideTranslationModal,
      translationModalState,
      selectedExternalIdForTranslate,
      translationCallback,
      referenceNameForTranslate,
      translationType,
    } = useModals();
    const { getTargetLanguages, getSourceLanguages, currentLanguageCode } = useTranslation();

    const selectedTargetLanguage = ref(null);
    const isTranslating = ref(false);
    const targetLanguages = ref([]);
    const sourceLanguages = ref([]);
    const glossaries = ref([]);
    const selectedGlossary = ref(null);
    // in case we use the modal from uploaded files view
    const selectedSourceLanguage = ref(null);

    const selectedSourceProvider = computed(
      () => sourceLanguages.value.find((l) => l.language === selectedSourceLanguage.value)?.provider ?? null,
    );
    // custom languages share their parent's `language` code, so the unique `key` identifies the selection
    const getTargetKey = (item) => item.key ?? item.language;

    const selectedTargetItem = computed(() =>
      targetLanguages.value.find((l) => getTargetKey(l) === selectedTargetLanguage.value),
    );
    const selectedTargetProvider = computed(() => selectedTargetItem.value?.provider ?? null);

    const disabledButton = computed(
      () =>
        !selectedTargetLanguage.value ||
        !selectedSourceLanguage.value ||
        // use the language code (not the custom key) and includes, to avoid translation from EN to EN (American)
        (selectedTargetItem.value?.language ?? selectedTargetLanguage.value).includes(selectedSourceLanguage.value),
    );

    const isOpened = computed({
      get: () => translationModalState.value,
      set: () => hideTranslationModal(),
    });

    const translationModes = [{ title: 'PDF' }, { title: 'Source document' }];

    const onTranslateDocument = async () => {
      const language = sourceLanguages.value.find((lang) => lang.language === selectedSourceLanguage.value);

      const provider = selectedTargetProvider.value;

      switch (translationType.value) {
        case TRANSLATION_TYPE.email:
          translateDocument(
            selectedExternalIdForTranslate.value,
            selectedSourceLanguage.value,
            language.name,
            referenceNameForTranslate.value,
            selectedTargetLanguage.value,
            selectedGlossary.value,
            translationType.value,
            provider,
          );
          break;
        case TRANSLATION_TYPE.file:
          await translationCallback.value();
          translateDocument(
            selectedExternalIdForTranslate.value,
            selectedSourceLanguage.value,
            language.name,
            referenceNameForTranslate.value,
            selectedTargetLanguage.value,
            selectedGlossary.value,
            '',
            provider,
          );
          break;
        default: {
          translateDocument(
            selectedExternalIdForTranslate.value,
            selectedSourceLanguage.value,
            language.name,
            referenceNameForTranslate.value,
            selectedTargetLanguage.value,
            selectedGlossary.value,
            '',
            provider,
          );
          break;
        }
      }
      isTranslating.value = true;
    };

    const onClose = () => {
      hideTranslationModal();
    };

    const createTargetLanguageLabel = (language) => {
      const { name, translated, isCustom } = language;
      const label = isCustom ? `${name} (${t('translation.custom')})` : name;
      return translated ? `${label} - ${t('translation.replaceExistingTranslation')}` : label;
    };

    onMounted(async () => {
      sourceLanguages.value = await getSourceLanguages();
      if (!filesModalState.value) {
        const resolved = resolveLanguageCodeForSourceList(currentLanguageCode.value, sourceLanguages.value);
        if (resolved !== currentLanguageCode.value) {
          selectedSourceLanguage.value = resolved;
        } else {
          selectedSourceLanguage.value = currentLanguageCode.value;
        }
      }
    });

    watch(selectedSourceLanguage, async (newSourceLanguage) => {
      if (newSourceLanguage) {
        targetLanguages.value = await getTargetLanguages(
          selectedExternalIdForTranslate.value,
          newSourceLanguage,
          selectedSourceProvider.value,
        );
      }
    });

    watch([selectedTargetLanguage, selectedSourceLanguage], async () => {
      selectedGlossary.value = null;
      glossaries.value = await getAvailableGlossaries(selectedTargetLanguage.value, selectedSourceLanguage.value);
    });

    return {
      targetLanguages,
      filesModalState,
      glossaries,
      isTranslating,
      isOpened,
      referenceNameForTranslate,
      selectedExternalIdForTranslate,
      selectedGlossary,
      selectedReference,
      selectedTargetLanguage,
      selectedSourceLanguage,
      sourceLanguages,
      onTranslateDocument,
      onClose,
      translationModes,
      createTargetLanguageLabel,
      disabledButton,
      getTargetKey,
      t,
    };
  },
};
</script>

<style scoped lang="scss">
.bb-select-list {
  :deep(.bb-select-list__label) {
    @include bb-regular-text-styles('sm');
    color: $bb-neutral-2;
  }

  :deep(.el-select .el-select__wrapper) {
    &.is-focused {
      border-color: $bb-primary-3;
    }
  }

  :deep(.el-select-dropdown__item) {
    background-color: $bb-neutral-0;
    border: none !important;

    .status-container .bb-select-list-item {
      &__label {
        display: flex;
        flex-direction: column;

        > span:nth-of-type(2) {
          @include bb-regular-text-styles('xs');
        }
      }
    }

    &.is-selected {
      color: $bb-primary-3 !important;
      background-color: $bb-neutral-0 !important;

      .status-container .bb-select-list-item {
        &__label {
          color: $bb-primary-3 !important;
        }
      }
    }
  }
}

.select-language {
  padding: 0 $bb-spacing-4 0 $bb-spacing-4;
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-2;
  width: 100%;

  .status-container {
    display: flex;
    gap: $bb-spacing-1;
    align-items: center;
  }

  .bb-select-list-item {
    &__label {
      @include bb-regular-text-styles('lg');
      color: $bb-neutral-1;
    }
  }

  .switch-translation-mode {
    margin-bottom: $bb-spacing-2;

    :deep(.switch-item) {
      background-color: $bb-neutral-0 !important;
    }

    :deep(.switch-item .v-slide-group__container .v-slide-group__content .v-tab--selected) {
      background-color: $bb-neutral-1;
    }

    :deep(.switch-item .v-slide-group__container .v-slide-group__content .v-tab) {
      &:active {
        background-color: $bb-neutral-1 !important;
      }

      &:hover {
        background-color: $bb-neutral-3 !important;
      }
    }
  }
}

.translating-message {
  display: flex;
  flex-flow: column;
}

.title-hint-container {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-1;
  padding-bottom: $bb-spacing-2;

  :deep(.body-1) {
    color: $bb-neutral-2;
  }
}

.close-button {
  align-self: end;
}

.document-title {
  @include bb-regular-text-styles('lg');
}

.actions-container {
  display: flex;
  gap: $bb-spacing-1;
}
</style>
